import { Drawer } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AutoCompleteComponent from "../../../../common/components/AutoComplete";
import EditFormInput from "../../../../common/components/EditableText/EditableFormInput";
import ScrollWrapper from "../../../../common/components/ScrollBar";
import SubDataTable from "../../../../common/components/SubDataTable";
import { deleteReceivingOrder, getAllReceivingOrders, setInventoryData } from "../../../../store/actions";

const categories = {
  angiographic: "Angiographic Supply",
  narcotic: "Narcotic Inventory",
  other: "Other Inventory",
};

const titleStyle = {
  textAlign: "left",
  paddingLeft: "11px",
  color: "rgba(0, 0, 0, 0.65)",
  fontWeight: 600,
  borderLeft: "1px solid #dfe3e6",
  display: "flex",
  height: "100%",
  alignItems: "center",
  fontSize: "14px",
};

const filterSuggestion = (suggestions, userInput) => {
  let filteredSuggestions = [];
  if (!userInput) {
    filteredSuggestions = suggestions;
  }
  if (userInput) {
    filteredSuggestions = suggestions.filter((suggestion) => suggestion.toLowerCase().indexOf(userInput.toLowerCase()) > -1);
  }
  return filteredSuggestions;
};

const getTitle = () => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <div style={{ ...titleStyle, width: "50%", paddingLeft: 0, borderLeft: 0 }}>Date</div>
      <div style={{ ...titleStyle, width: "50%", textAlign: "left" }}>Cost Per Item</div>
    </div>
  );
};

const HistorySidebar = React.forwardRef(({ resource, isShow, handleCloseSidebar }, ref) => {
  const orderHistory = useSelector((state) => state.inventory.history);
  const costHistory = useSelector((state) => state.inventory.inventoryCostHistory);
  const isDeleted = useSelector((state) => state.inventory.isDeleted);
  const sideNavStatus = useSelector((state) => state.common.sideNavStatus);

  const [notes, setNotes] = useState([]);
  const [searchInput, setInput] = useState("");
  const [openDelete, setOpenDelete] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (orderHistory) {
      const initialNotes = orderHistory.notes ? orderHistory.notes.split(",") : [];
      setNotes(initialNotes);
    }
  }, [orderHistory]);

  useEffect(() => {
    if (isDeleted) {
      dispatch(getAllReceivingOrders());
      dispatch(setInventoryData("isDeleted", false));
      dispatch(setInventoryData("history", null));
      setOpenDelete(false);
    }
  }, [isDeleted]);

  const noteRows = useMemo(() => {
    if (searchInput) {
      return filterSuggestion(notes, searchInput);
    }
    return notes;
  }, [notes, searchInput]);

  const handleSearch = (search) => {
    setInput(search);
  };
  const addNote = () => {
    const newNotes = [...notes, searchInput];
    setNotes(newNotes);
    setInput("");
  };

  const removeNote = (row, index) => {
    const newNotes = [...notes];
    newNotes.splice(index, 1);
    setNotes(newNotes);
  };

  const deletePurchase = () => {
    if (orderHistory) {
      dispatch(deleteReceivingOrder(orderHistory?.id));
    }
  };

  const cost_per_item = useMemo(() => {
    if (orderHistory && orderHistory.cost) {
      const quantity = orderHistory.quantity ? orderHistory.quantity : 1;
      return (orderHistory.cost / quantity).toFixed(2);
    }
    return null;
  }, [orderHistory]);

  const costRows = useMemo(() => {
    if (costHistory && costHistory.length) {
      return costHistory.map((ch) => ({
        item: ch.created_at ? moment(ch.created_at).format("MM/DD/YYYY") : undefined,
        amount: "$" + parseFloat(ch.cost_per_item).toFixed(2),
      }));
    }
    if (cost_per_item) {
      return [
        {
          item: moment().format("MM/DD/YYYY"),
          amount: "$" + parseFloat(cost_per_item).toFixed(2),
        },
      ];
    }
    return [];
  }, [costHistory]);

  if (!orderHistory) {
    return null;
  }

  return (
    <Drawer
      title={orderHistory?.name || " "}
      placement="right"
      closable
      className={sideNavStatus ? "wide" : ""}
      onClose={handleCloseSidebar}
      visible={isShow}
      destroyOnClose
      key="right"
    >
      <div ref={ref} className="resourceContainer new">
        <ScrollWrapper>
          {!openDelete && (
            <div
              className="resourceContent"
              style={{
                flex: "unset",
                paddingLeft: "24px",
                paddingRight: "20px",
              }}
            >
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Product No"} value={orderHistory.product_number} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Code No"} value={orderHistory.code_number} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Category"} value={categories[orderHistory.type]} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Brand"} value={orderHistory.brand} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"PAR Level"} value={orderHistory.par_level} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Bin Rate"} value={orderHistory.bin_rate} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Location"} value={orderHistory.location} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Specific Location"} value={orderHistory.specific_location} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Supplier E-Mail"} value={orderHistory.supplier_email} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Supplier Fax"} value={orderHistory.supplier_fax} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Quantity per unit"} value={orderHistory.quantity} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Unit"} value={orderHistory.unit} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Cost"} value={orderHistory.cost && parseFloat(orderHistory.cost).toFixed(2)} prefix={"$"} />
              </div>
              <div className="resourceInfo d-flex">
                <EditFormInput label={"Cost/Item"} value={cost_per_item} prefix={"$"} />
              </div>
              <div className="resourceInfo d-flex" style={{ marginTop: "0.5rem" }}>
                <b style={{ minWidth: "35%" }}>Cost History </b>
              </div>
              <div className="resourceInfo">
                <SubDataTable title={getTitle()} data={costRows} half={true} />
              </div>
              <hr />
              <div className="resourceInfo d-flex">
                <b style={{ minWidth: "35%" }}>Notes </b>
              </div>
              <div className="resourceInfo d-flex">
                <AutoCompleteComponent
                  title="purchase-inventory-notes"
                  rows={noteRows}
                  removeItem={removeNote}
                  addItem={addNote}
                  handleSearch={handleSearch}
                  disableOptions={true}
                  placeholder={"Information Note"}
                  disableSearchIcon={true}
                />
              </div>
              <div className="resourceInfo d-flex" style={{ marginTop: "2rem", alignItems: "center" }}>
                <b style={{ marginRight: "0.25rem" }}>Purchase </b>
                <div
                  style={{
                    width: "110px",
                    minWidth: "110px",
                    maxWidth: "110px",
                  }}
                >
                  <EditFormInput label={"Number"} value={orderHistory.purchase_number} />
                </div>
                <b style={{ marginLeft: "0.25rem" }}>{` at ${orderHistory.cost ? "$" + parseFloat(orderHistory.cost).toFixed(2) : ""} ${
                  orderHistory.unit || ""
                }`}</b>
              </div>
              <hr />
            </div>
          )}

          {!openDelete && (
            <div
              className="actionContent"
              style={{
                marginTop: "1.5rem",
                paddingRight: "1rem",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                style={{
                  marginTop: "0.5rem",
                }}
                className="outlined-btn"
                onClick={() => setOpenDelete(true)}
              >
                Remove
              </button>
            </div>
          )}

          {openDelete && (
            <>
              <div className="resourceInfo">
                <b style={{ width: "100%", textAlign: "center" }}>Do you really want to delete this Purchase?</b>
              </div>
              <div
                className="actionContent"
                style={{
                  marginTop: "1.5rem",
                  paddingRight: "1rem",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <button className="outlined-btn" style={{ marginTop: "0.5rem" }} onClick={() => setOpenDelete(false)}>
                  Back
                </button>
                <button onClick={deletePurchase} className="outlined-btn" style={{ marginTop: "0.5rem", marginLeft: "0.5rem" }}>
                  Delete
                </button>
              </div>
            </>
          )}
        </ScrollWrapper>
      </div>
    </Drawer>
  );
});

export default HistorySidebar;
